// src/redux/AnalyticsSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { unauthorizedError } from "../../utils/helpers";
import AnalyticsService from "../../services/api/analyticsService";
import { setLoading } from "./authSlice";
import { setSessionExpired } from "./globalSlice";
import { toast } from "react-toastify";

const analytics = new AnalyticsService();

export const getAnalyticsData = createAsyncThunk(
  `/analytics`,
  async ({ startDate, endDate, navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getAnalyticsData({
        startDate,
        endDate,
        navigate,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);
export const getAnalyticsDataByOrgId = createAsyncThunk(
  `/analytics/orgId`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getAnalyticsByOrgId({
        startDate,
        endDate,
        orgId,
        navigate,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);

// export const exportDashboardReport = createAsyncThunk(
//   `/analytics/export-dashboard-report`,
//   async({startDate, endDate, navigate},{dispatch})=>{
//     try {
//       const response = await analytics.getExportDashboardReportData({
//         startDate,
//         endDate,
//         navigate,
//       })
//       return response;
//     } catch (error) {
//       if (error?.response) {
//         unauthorizedError(navigate);
//         dispatch(setSessionExpired(true));
//       }
//       throw error;
//     }
//   }
// )

export const getBillingAnalyticsData = createAsyncThunk(
  `/unit-analytics`,
  async ({ startDate, endDate, navigate }, { dispatch, signal }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getBillingAnalyticsData({
        startDate,
        endDate,
        navigate,
        signal,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);

export const getBillingAnalyticsDataByOrgId = createAsyncThunk(
  `/unit-analytics/orgId`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch, signal }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getBillingAnalyticsDataByOrg({
        startDate,
        endDate,
        orgId,
        navigate,
        signal,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      toast.error(error?.data.error);
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);
export const getActionWiseAnalytics = createAsyncThunk(
  `/action-wise-analytics`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getActionWiseAnalytics({
        startDate,
        endDate,
        orgId,
        navigate,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);

export const getTotalActionStats = createAsyncThunk(
  `/total-action-stats`,
  async ({ orgId, navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getTotalActionStats({
        orgId,
        navigate,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);

export const getDashboardData = createAsyncThunk(
  `/dashboard-data`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch, signal }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getDashboardData({
        startDate,
        endDate,
        orgId,
        navigate,
        signal,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);

export const getActiveUsers = createAsyncThunk(
  `/active-users`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getActiveUsers({
        startDate,
        endDate,
        orgId,
        navigate,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (err) {
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw err;
    }
  }
);

export const getUsersPerAction = createAsyncThunk(
  `/users-per-action`,
  async({startDate, endDate, action, navigate}, { dispatch })=>{
    try{
      const response = await analytics.getUsersPerAction({
        startDate,
        endDate,
        action,
        navigate
      });
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (err) {
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw err;
    }
  }
)

export const getUserWiseActions = createAsyncThunk(
  `/user-wise-actions`,
  async ({ startDate, endDate, navigate }, { dispatch, signal }) => {
    try {
      const response = await analytics.getUserWiseActions({
        startDate,
        endDate,
        navigate,
        signal,
      });
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw err;
    }
  }
);

export const getActionWisePerformance = createAsyncThunk(
  `/action-wise-performance`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch, signal }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getActionWisePerformance({
        startDate,
        endDate,
        orgId,
        navigate,
        signal,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw err;
    }
  }
);

export const getFeedbackStats = createAsyncThunk(
  `/feedback-stats`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch, signal }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getFeedbackStats({
        startDate,
        endDate,
        orgId,
        navigate,
        signal,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw err;
    }
  }
);

export const getFeedbackActionWiseAnalytics = createAsyncThunk(
  `/feedback-action-wise-analytics`,
  async ({ startDate, endDate, orgId, navigate }, { dispatch, signal }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getFeedbackActionWiseAnalytics({
        startDate,
        endDate,
        orgId,
        navigate,
        signal,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw err;
    }
  }
);



export const getNotSatisfiedRequests = createAsyncThunk(
  `/not-satisfied-requests`,
  async ({ orgId, startDate, endDate, page = 1, limit = 20, name = "", action = "",navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.getNotSatisfiedRequests({
        orgId,
        startDate,
        endDate,
        page,
        limit,
        name,
        action,
        navigate,
      });
      dispatch(setLoading(false));
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (err) {
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw err;
    }
  }
);

export const addAdminComment = createAsyncThunk(
  `/feedback/add-comment`,
  async ({ id, comment, navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await analytics.addAdminComment({ id, comment });
      dispatch(setLoading(false));
      toast.success("Comment added successfully");
      return response;
    } catch (err) {
      if (err?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      dispatch(setLoading(false));
      throw err;
    }
  }
);

const AnalyticsSlice = createSlice({
  name: "Analytics",
  initialState: {
    loader: false,
    analyticsData: null,
    billingAnalyticsApiData: null,
    billingAnalyticsOrgData: null,
    broadcastUserCount: null,
    monthlyActiveUsersCount: null,
    selectedKey: "data1",
    searchItemAnalytics: "",
    searchItemResponse: "",
    actionWiseAnalytics: "null",
    totalActionStats: "null",
    dashboardData: null,
    activeUsers: null,
    usersPerAction: null,
    usersPerActionLoading: false,
    userWiseActions: null,
    userWiseActionsLoading: false,
    actionWisePerformance: null,
    feedbackStats: null,
    feedbackActionWiseAnalytics: null,
    notSatisfiedRequests: null,
  },
  reducers: {
    setAnalyticsData: (state, action) => {
      state.analyticsData = action.payload;
    },
    setBillingAnalyticsData: (state, action) => {
      state.billingAnalyticsApiData = action.payload;
    },
    setSelectedKey: (state, action) => {
      state.selectedKey = action.payload;
    },
    setSearchItemAnalytics: (state, action) => {
      state.searchItemAnalytics = action.payload;
    },
    setSearchItemResponse: (state, action) => {
      state.searchItemResponse = action.payload;
    },
    setUsersPerActionLoading: (state, action)=>{
      state.usersPerActionLoading = action.payload;
    },
    setUserWiseActionsLoading: (state, action)=>{
      state.userWiseActionsLoading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // analytics
      .addCase(getAnalyticsData.pending, (state) => {
        state.loader = true;
      })
      .addCase(getAnalyticsData.fulfilled, (state, action) => {
        state.loader = false;
        state.analyticsData = action.payload;
      })
      .addCase(getAnalyticsData.rejected, (state) => {
        state.loader = false;
      })
      // analytics by org
      .addCase(getAnalyticsDataByOrgId.pending, (state) => {
        state.loader = true;
      })
      .addCase(getAnalyticsDataByOrgId.fulfilled, (state, action) => {
        state.loader = false;
        state.analyticsData = action.payload;
      })
      .addCase(getAnalyticsDataByOrgId.rejected, (state) => {
        state.loader = false;
      })
      // unit-analytics
      .addCase(getBillingAnalyticsData.pending, (state) => {
        state.loader = true;
      })
      .addCase(getBillingAnalyticsData.fulfilled, (state, action) => {
        state.loader = false;
        state.billingAnalyticsApiData = action.payload.api[0];
        state.billingAnalyticsOrgData = action.payload.orgUserMetrics;
        state.billingAnalyticsActiveUsers = action.payload.activeUserCount;
        state.broadcastUserCount = action.payload.broadcastUserCount;
        state.monthlyActiveUsersCount = action.payload.monthlyActiveUsersCount;
      })
      .addCase(getBillingAnalyticsData.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loader = false;
      })
      // unit-analytics by org
      .addCase(getBillingAnalyticsDataByOrgId.pending, (state) => {
        state.loader = true;
      })
      .addCase(getBillingAnalyticsDataByOrgId.fulfilled, (state, action) => {
        state.loader = false;
        state.billingAnalyticsApiData = action.payload.api[0];
        state.billingAnalyticsOrgData = action.payload.orgUserMetrics;
        state.billingAnalyticsActiveUsers = action.payload.activeUserCount;
        state.broadcastUserCount = action.payload.broadcastUserCount;
        state.monthlyActiveUsersCount = action.payload.monthlyActiveUsersCount;
      })
      .addCase(getBillingAnalyticsDataByOrgId.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loader = false;
      })
      .addCase(getActionWiseAnalytics.pending, (state) => {
        state.loader = true;
      })
      .addCase(getActionWiseAnalytics.fulfilled, (state, action) => {
        state.loader = false;
        state.actionWiseAnalytics = action.payload;
      })
      .addCase(getActionWiseAnalytics.rejected, (state) => {
        state.loader = false;
      })
      .addCase(getTotalActionStats.pending, (state) => {
        state.loader = true;
      })
      .addCase(getTotalActionStats.fulfilled, (state, action) => {
        state.loader = false;
        state.totalActionStats = action.payload;
      })
      .addCase(getTotalActionStats.rejected, (state) => {
        state.loader = false;
      })
      .addCase(getDashboardData.pending, (state) => {
        state.loader = true;
      })
      .addCase(getDashboardData.fulfilled, (state, action) => {
        state.loader = false;
        state.dashboardData = action.payload;
      })
      .addCase(getDashboardData.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loader = false;
      })
      .addCase(getActiveUsers.pending, (state) => {
        state.loader = true;
      })
      .addCase(getActiveUsers.fulfilled, (state, action) => {
        state.loader = false;
        state.activeUsers = action.payload;
      })
      .addCase(getActiveUsers.rejected, (state) => {
        state.loader = false;
      })
      .addCase(getUsersPerAction.pending, (state) => {
        state.usersPerActionLoading = true;
      })
      .addCase(getUsersPerAction.fulfilled, (state, action) => {
        state.usersPerActionLoading = false;
        state.usersPerAction = action.payload?.data;
      })
      .addCase(getUsersPerAction.rejected, (state) => {
        state.usersPerActionLoading = false;
      })
      .addCase(getUserWiseActions.pending, (state) => {
        state.userWiseActionsLoading = true;
      })
      .addCase(getUserWiseActions.fulfilled, (state, action) => {
        state.userWiseActionsLoading = false;
        state.userWiseActions = action.payload;
      })
      .addCase(getUserWiseActions.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.userWiseActionsLoading = false;
      })
      .addCase(getActionWisePerformance.pending, (state) => {
        state.loader = true;
      })
      .addCase(getActionWisePerformance.fulfilled, (state, action) => {
        state.loader = false;
        state.actionWisePerformance = action.payload;
      })
      .addCase(getActionWisePerformance.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loader = false;
      })
      .addCase(getFeedbackStats.pending, (state) => {
        state.loader = true;
      })
      .addCase(getFeedbackStats.fulfilled, (state, action) => {
        state.loader = false;
        state.feedbackStats = action.payload?.data || action.payload;
      })
      .addCase(getFeedbackStats.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loader = false;
      })
      .addCase(getFeedbackActionWiseAnalytics.pending, (state) => {
        state.loader = true;
      })
      .addCase(getFeedbackActionWiseAnalytics.fulfilled, (state, action) => {
        state.loader = false;
        state.feedbackActionWiseAnalytics = action.payload?.data || action.payload;
      })
      .addCase(getFeedbackActionWiseAnalytics.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loader = false;
      })
      .addCase(getNotSatisfiedRequests.pending, (state) => {
        state.loader = true;
      })
      .addCase(getNotSatisfiedRequests.fulfilled, (state, action) => {
        state.loader = false;
        // Store the full response object (contains both data and pagination)
        state.notSatisfiedRequests = action.payload;
      })
      .addCase(getNotSatisfiedRequests.rejected, (state) => {
        state.loader = false;
      })
      .addCase(addAdminComment.pending, (state) => {
        state.loader = true;
      })
      .addCase(addAdminComment.fulfilled, (state) => {
        state.loader = false;
      })
      .addCase(addAdminComment.rejected, (state) => {
        state.loader = false;
      });
  },
});

export const {
  setAnalyticsData,
  setBillingAnalyticsData,
  setSelectedKey,
  setSearchItemAnalytics,
  setSearchItemResponse,
} = AnalyticsSlice.actions;

export default AnalyticsSlice.reducer;
